// @flow

import React, {useState, useEffect, useRef} from "react";
import {Col, Table, Form, Input, Switch, Button, Checkbox, Tooltip} from "antd";
import {useFormik, FormikProvider} from "formik";
import {useHistory} from "react-router-dom";
import {InfoCircleOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";

import {ErrorFocus} from "../../_defaultComponents/ErrorFocus/ErrorFocus";
import {
    CompanyContanier,
    CompanyButtonContainer,
    SubCompanyContainer,
    FilterContainer
} from "./optionsStyles";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {CityFilter} from "../../_defaultComponents/FilterComponents";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";

const {Column} = Table;

export const SmsTrackingOptions = ({companyId}) => {
    const stores = useStore();
    const history = useHistory();
    const {t} = useTranslation(["smsTrackingOptions"]);

    const [currentPage, setCurrentPage] = useState(1);
    const [isCompanyFormChanged, setIsCompanyFormChanged] = useState(false);
    const [companyFormData, setCompanyFormData] = useState();
    const [city, setCity] = useState("all");
    const [searchText, setSearchText] = useState("");
    const [text, setText] = useState("");
    const [showSubmitButton, setShowSubmitButton] = useState({});
    const [isSubCompanySubmitting, setIsSubCompanySubmitting] = useState(false);
    const [companyRecall, setCompanyRecall] = useState(false);
    const [subCompanyRecall, setSubCompanyRecall] = useState(false);

    const companyRecallRef = useRef(companyRecall);
    companyRecallRef.current = companyRecall;
    const subCompanyRecallRef = useRef(subCompanyRecall);
    subCompanyRecallRef.current = subCompanyRecall;

    const [companyLoading, companyData, setCompanyData, setCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.companyStore.getCompany.bind(null, {
            companyId,
            withSubCompanies: false,
            vehicleType: null
        }),
        dependencies: [companyRecall]
    });

    const [subCompanyLoading, subCompanyData, setSubCompanyData, setSubCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn:
            companyData.data && companyData.data._id
                ? stores.companyStore.getSubCompanyList.bind(null, {
                      companyId: companyData.data._id,
                      limit: 10,
                      skip: (currentPage - 1) * 10,
                      activity: "enabled",
                      vehicleType: null,
                      city,
                      searchText
                  })
                : () => [],
        dependencies: [companyData, currentPage, subCompanyRecall, city, searchText]
    });

    const formik = useFormik({
        initialValues: {
            options: {
                smsTracking: {
                    isActive: companyData?.data?.options?.smsTracking?.isActive,
                    isSendOnFirstOrder: companyData?.data?.options?.smsTracking?.isSendOnFirstOrder,
                    isSendOnEachOrder: companyData?.data?.options?.smsTracking?.isSendOnEachOrder,
                    lagMarginMinute: 60,
                    applySmsTrackingOptions: false
                }
            },
            ...(companyData && companyData.data)
        },
        enableReinitialize: true,
        validateOnChange: true,
        onSubmit: async values => {
            setCompanyLoading(true);

            let filters = {};
            filters.searchText = searchText;
            filters.city = city;
            if (Object.keys(filters).length > 0) {
                values.filters = filters;
            }

            await stores.companyStore.updateCompanyTrackingOptions(values);
            if (values.isActive) {
                setSubCompanyRecall(!subCompanyRecallRef.current);
            }
            setCompanyRecall(!companyRecallRef.current);
            setCompanyLoading(false);
            setIsCompanyFormChanged(false);
        }
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue
    } = formik;

    useEffect(() => {
        if (values && !isCompanyFormChanged) {
            setCompanyFormData(values);
        } else if (values && JSON.stringify(companyFormData) !== JSON.stringify(values)) {
            setIsCompanyFormChanged(true);
        } else {
            setIsCompanyFormChanged(false);
        }
    }, [values]);

    const onSubCompanySubmit = async index => {
        setIsSubCompanySubmitting(true);
        let submitData = subCompanyData.rows[index]?.options?.smsTracking;
        submitData.name = subCompanyData.rows[index]?.name;
        submitData._id = subCompanyData.rows[index]?._id;
        const params = {
            subCompanyId: submitData._id,
            options: {
                isActive: submitData.isActive,
                isSendOnFirstOrder: submitData.isSendOnFirstOrder,
                isSendOnEachOrder: submitData.isSendOnEachOrder,
                lagMarginMinute: submitData.lagMarginMinute
            }
        };
        const result = await stores.companyStore.updateSubCompanyTrackingOptions(params);
        if (result) {
            setSubCompanyRecall(!subCompanyRecallRef.current);
        }
        setIsSubCompanySubmitting(false);
    };

    // cross check sms sending methods, if both are selected then show error
    const validateSmsSendingMethods = ({isSendOnFirstOrder, isSendOnEachOrder}) => {
        if (isSendOnFirstOrder && isSendOnEachOrder) {
            stores.appStore.openInfoModal({
                content: t("smsTrackingOptions.validations.smsSendingMethods.onlyOne", {
                    ns: "smsTrackingOptions"
                }),
                onOk: () => {}
            });

            return false;
        }
        return true;
    };

    const singleOrderSmsTooltipTitle = t(
        "smsTrackingOptions.fields.singleOrderNotification.description",
        {ns: "smsTrackingOptions"}
    );
    const multipleOrderSmsTooltipTitle = t(
        "smsTrackingOptions.fields.multipleOrderNotification.description",
        {ns: "smsTrackingOptions"}
    );
    const lagMarginTooltipTitle = t("smsTrackingOptions.fields.delayRange.description", {
        ns: "smsTrackingOptions"
    });
    return (
        <div data-testid="smsTrackingOptionsScreen">
            <h2 data-testid="companyName">{companyData?.data?.companyName}</h2>
            <CompanyContanier>
                <Form
                    autoComplete="off"
                    data-testid="smsTrackingOptionsForm"
                    onFinish={handleSubmit}
                    onChange={e => setIsCompanyFormChanged(true)}
                >
                    <Table
                        loading={companyLoading}
                        dataSource={[companyData.data]}
                        rowKey="_id"
                        data-testid="smsTrackingOptionsTable"
                        pagination={false}
                        locale={{
                            emptyText: <EmptyMessage />
                        }}
                    >
                        <ErrorFocus formik={formik} />
                        <FormikProvider value={formik}>
                            <Column
                                title={`${t("smsTrackingOptions.fields.smsTrackingOptions.label", {
                                    ns: "smsTrackingOptions"
                                })} (${t("smsTrackingOptions.fields.smsTrackingOptions.short", {
                                    ns: "smsTrackingOptions"
                                })})`}
                                dataIndex="isActive"
                                render={text => (
                                    <Form.Item>
                                        <Switch
                                            checked={values.options.smsTracking?.isActive}
                                            checkedChildren={t("other.active", {ns: "common"})}
                                            unCheckedChildren={t("other.passive", {ns: "common"})}
                                            data-testid="smsTrackingOptionSwitch"
                                            onChange={e => {
                                                setFieldValue("options.smsTracking.isActive", e);
                                                setIsCompanyFormChanged(true);
                                            }}
                                        />
                                    </Form.Item>
                                )}
                                align="left"
                            />
                            <Column
                                title={
                                    <Tooltip title={singleOrderSmsTooltipTitle}>
                                        {t(
                                            "smsTrackingOptions.fields.singleOrderNotification.label",
                                            {ns: "smsTrackingOptions"}
                                        )}{" "}
                                        (
                                        {t(
                                            "smsTrackingOptions.fields.singleOrderNotification.short",
                                            {ns: "smsTrackingOptions"}
                                        )}
                                        ) <InfoCircleOutlined />
                                    </Tooltip>
                                }
                                dataIndex="isSendOnEachOrder"
                                render={text => (
                                    <Form.Item>
                                        <Switch
                                            checked={values.options.smsTracking?.isSendOnEachOrder}
                                            checkedChildren={t("other.active", {ns: "common"})}
                                            unCheckedChildren={t("other.passive", {ns: "common"})}
                                            data-testid="singleOrderSmsTrackingOptionSwitch"
                                            onChange={e => {
                                                if (
                                                    !validateSmsSendingMethods({
                                                        isSendOnFirstOrder:
                                                            values.options.smsTracking
                                                                ?.isSendOnFirstOrder,
                                                        isSendOnEachOrder: e
                                                    })
                                                ) {
                                                    return;
                                                }
                                                setFieldValue(
                                                    "options.smsTracking.isSendOnEachOrder",
                                                    e
                                                );
                                                setIsCompanyFormChanged(true);
                                            }}
                                        />
                                    </Form.Item>
                                )}
                                align="left"
                            />
                            <Column
                                title={
                                    <Tooltip title={multipleOrderSmsTooltipTitle}>
                                        {t(
                                            "smsTrackingOptions.fields.multipleOrderNotification.label",
                                            {ns: "smsTrackingOptions"}
                                        )}{" "}
                                        (
                                        {t(
                                            "smsTrackingOptions.fields.multipleOrderNotification.short",
                                            {ns: "smsTrackingOptions"}
                                        )}
                                        ) <InfoCircleOutlined />
                                    </Tooltip>
                                }
                                dataIndex="isSendOnFirstOrder"
                                render={text => (
                                    <Form.Item>
                                        <Switch
                                            checked={values.options.smsTracking?.isSendOnFirstOrder}
                                            checkedChildren={t("other.active", {ns: "common"})}
                                            unCheckedChildren={t("other.passive", {ns: "common"})}
                                            data-testid="multipleOrderSmsTrackingOptionSwitch"
                                            onChange={e => {
                                                if (
                                                    !validateSmsSendingMethods({
                                                        isSendOnFirstOrder: e,
                                                        isSendOnEachOrder:
                                                            values.options.smsTracking
                                                                ?.isSendOnEachOrder
                                                    })
                                                ) {
                                                    return;
                                                }
                                                setFieldValue(
                                                    "options.smsTracking.isSendOnFirstOrder",
                                                    e
                                                );
                                                setIsCompanyFormChanged(true);
                                            }}
                                        />
                                    </Form.Item>
                                )}
                                align="left"
                            />
                            <Column
                                title={
                                    <Tooltip title={lagMarginTooltipTitle}>
                                        {t("smsTrackingOptions.fields.delayRange.label", {
                                            ns: "smsTrackingOptions"
                                        })}{" "}
                                        (
                                        {t("smsTrackingOptions.fields.delayRange.short", {
                                            ns: "smsTrackingOptions"
                                        })}
                                        ) <InfoCircleOutlined />
                                    </Tooltip>
                                }
                                dataIndex="lagMarginMinute"
                                render={text => (
                                    <Form.Item>
                                        <Input
                                            type="number"
                                            name="lagMarginMinute"
                                            data-testid="lagMarginMinuteInput"
                                            defaultValue={0}
                                            value={
                                                values.options.smsTracking?.lagMarginMinute || 60
                                            }
                                            onChange={e => {
                                                setFieldValue(
                                                    "options.smsTracking.lagMarginMinute",
                                                    e.target.value < 0 ? 0 : e.target.value
                                                );
                                            }}
                                        />
                                    </Form.Item>
                                )}
                                align="left"
                            />
                        </FormikProvider>
                    </Table>
                    <CompanyButtonContainer justify="end" align="middle">
                        <Checkbox
                            data-testid="applyForAllSubCompaniesCheckbox"
                            checked={values?.applySmsTrackingOptions}
                            onChange={e =>
                                setFieldValue("applySmsTrackingOptions", e.target.checked)
                            }
                        ></Checkbox>
                        <Button
                            htmlType="submit"
                            disabled={!isCompanyFormChanged || isSubmitting}
                            type="primary"
                            data-testid="submitButton"
                        >
                            {t("actions.save", {ns: "common"})}
                        </Button>
                    </CompanyButtonContainer>
                </Form>
            </CompanyContanier>
            <Col span={12}>
                <FilterContainer type="flex" gutter={[20, 20]}>
                    <Col span={6}>
                        <CityFilter
                            value={city}
                            onChange={value => {
                                setCurrentPage(1);
                                setCity(value);
                            }}
                        />
                    </Col>
                    <Col span={6}>
                        <h5>{t("filters.search", {ns: "common"})}</h5>
                        <Input.Search
                            data-testid="searchInput"
                            value={text}
                            onSearch={value => {
                                setCurrentPage(1);
                                setSearchText(value);
                            }}
                            onChange={event => {
                                setText(event.target.value);
                            }}
                        />
                    </Col>
                </FilterContainer>
            </Col>
            <SubCompanyContainer>
                <Table
                    rowKey="_id"
                    data-testid="subCompaniesTable"
                    dataSource={subCompanyData?.rows}
                    loading={subCompanyLoading}
                    pagination={{
                        total: subCompanyData.dataCount,
                        current: currentPage,
                        onChange: async page => {
                            setCurrentPage(page);
                            history.push(
                                "/companies/sms-tracking-options/" + companyId + "?page=" + page
                            );
                        },
                        hideOnSinglePage: true,
                        showSizeChanger: false
                    }}
                    locale={{
                        emptyText: <EmptyMessage />
                    }}
                >
                    <Column
                        dataIndex="name"
                        title={t("fields.subcompany", {ns: "common"})}
                        render={text => <span data-textid="name">{text}</span>}
                    />
                    <Column
                        dataIndex="city"
                        title={t("fields.city", {ns: "common"})}
                        render={text => <span data-textid="city">{text}</span>}
                    />
                    <Column
                        dataIndex="isActive"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={t("smsTrackingOptions.fields.smsTrackingOptions.short", {
                            ns: "smsTrackingOptions"
                        })}
                        render={(text, record, index) => {
                            return (
                                <Switch
                                    data-testid="smsTrackingOptionSwitch"
                                    checked={
                                        subCompanyData.rows[index]?.options?.smsTracking?.isActive
                                    }
                                    onChange={e => {
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.smsTracking.isActive = e;
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="isSendOnEachOrder"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={
                            <Tooltip title={singleOrderSmsTooltipTitle}>
                                {t("smsTrackingOptions.fields.singleOrderNotification.short", {
                                    ns: "smsTrackingOptions"
                                })}{" "}
                                <InfoCircleOutlined />
                            </Tooltip>
                        }
                        render={(text, record, index) => {
                            return (
                                <Switch
                                    data-testid="singleOrderSmsTrackingOptionSwitch"
                                    checked={
                                        subCompanyData.rows[index]?.options?.smsTracking
                                            ?.isSendOnEachOrder
                                    }
                                    onChange={e => {
                                        if (
                                            !validateSmsSendingMethods({
                                                isSendOnEachOrder: e,
                                                isSendOnFirstOrder:
                                                    subCompanyData.rows[index]?.options?.smsTracking
                                                        ?.isSendOnFirstOrder
                                            })
                                        ) {
                                            return;
                                        }

                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.smsTracking.isSendOnEachOrder = e;
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="isSendOnFirstOrder"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={
                            <Tooltip title={multipleOrderSmsTooltipTitle}>
                                {t("smsTrackingOptions.fields.multipleOrderNotification.short", {
                                    ns: "smsTrackingOptions"
                                })}{" "}
                                <InfoCircleOutlined />
                            </Tooltip>
                        }
                        render={(text, record, index) => {
                            return (
                                <Switch
                                    data-testid="multipleOrderSmsTrackingOptionSwitch"
                                    checked={
                                        subCompanyData.rows[index]?.options?.smsTracking
                                            ?.isSendOnFirstOrder
                                    }
                                    onChange={e => {
                                        if (
                                            !validateSmsSendingMethods({
                                                isSendOnEachOrder:
                                                    subCompanyData.rows[index]?.options?.smsTracking
                                                        ?.isSendOnEachOrder,
                                                isSendOnFirstOrder: e
                                            })
                                        ) {
                                            return;
                                        }
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.smsTracking.isSendOnFirstOrder = e;
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="lagMarginMinute"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        title={
                            <Tooltip title={lagMarginTooltipTitle}>
                                {t("smsTrackingOptions.fields.delayRange.short", {
                                    ns: "smsTrackingOptions"
                                })}{" "}
                                <InfoCircleOutlined />
                            </Tooltip>
                        }
                        render={(text, record, index) => {
                            return (
                                <Input
                                    type="number"
                                    name="lagMarginMinute"
                                    data-testid="lagMarginMinuteInput"
                                    defaultValue={0}
                                    value={
                                        subCompanyData.rows[index]?.options?.smsTracking
                                            ?.lagMarginMinute || 60
                                    }
                                    onChange={e => {
                                        showSubmitButton[record._id] = true;
                                        setSubCompanyData(prevData => {
                                            const newData = {...prevData};
                                            const newItem = {...newData.rows[index]};
                                            newItem.options.smsTracking.lagMarginMinute =
                                                e.target.value < 0 ? 0 : e.target.value;
                                            newData.rows[index] = newItem;
                                            return newData;
                                        });
                                    }}
                                />
                            );
                        }}
                    />
                    <Column
                        dataIndex="_id"
                        shouldCellUpdate={(record, prevRecord) => {
                            return true;
                        }}
                        render={(text, record, index) =>
                            showSubmitButton[text] && (
                                <Button
                                    disabled={isSubCompanySubmitting}
                                    onClick={() => onSubCompanySubmit(index)}
                                    type="primary"
                                    data-testid="submitButton"
                                >
                                    {t("actions.save", {ns: "common"})}
                                </Button>
                            )
                        }
                    />
                </Table>
            </SubCompanyContainer>
        </div>
    );
};
